import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import classnames from 'classnames'
import React, { useRef } from 'react'
import type { UploadOptions } from '@paintscout/util/s3'
import { PERMITTED_FILE_TYPES } from '@paintscout/util/s3'
import Button from '@paintscout/ui/components/Button'
import Dropzone from '@paintscout/ui/components/Dropzone'
import FilePreview from '@paintscout/ui/components/FilePreview'
import InputLabel from '@paintscout/ui/components/InputLabel'
import Spinner from '@paintscout/ui/components/Spinner'
import type { StyleClasses } from '@paintscout/ui/styles'
import { useTranslation } from '@paintscout/ui'

import { useImageUpload } from './useImageUpload'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  label: {
    marginBottom: theme.typography.pxToRem(5)
  },
  dropzone: {
    position: 'relative',
    flexGrow: 1,
    maxHeight: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.md,
    '& $button': {
      // material-ui will spit out a warning about not being able to find $button, but it still works
      // https://github.com/mui-org/material-ui/issues/15511
      minHeight: (props: Partial<UploadImageProps>) => (props.buttonHeight ? props.buttonHeight : 0)
    }
  },
  button: {
    height: '100%'
  },
  above: {
    display: 'flex',
    alignItems: 'space-between'
  },
  clearButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-2),
    backgroundColor: 'transparent !important'
  },
  acceptDrop: {},
  rejectDrop: {
    '& $button': {
      borderColor: theme.paintscout.palette.brand.Neutral.P85
    }
  },
  previewOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    background: 'rgba(255,255,255,0.7)'
  },
  preview: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  img: {
    '&:not($disabled)': {
      cursor: 'pointer'
    },
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  avatar: {
    borderRadius: '50%',
    '& img': {
      maxHeight: '100%',
      maxWidth: '100%',
      borderRadius: '50%',
      objectFit: 'contain !important' as any,
      backgroundColor: '#FFFFFF'
    }
  },
  productImage: {
    maxHeight: '140px',
    maxWidth: '120px',
    '& img': {
      maxHeight: '140px',
      maxWidth: '120px'
    }
  },
  disabled: {},
  filePreview: {
    border: `1px dashed transparent`,
    height: '110px',
    '&:hover': {
      cursor: (props: Partial<UploadImageProps>) => (!props.disabled ? 'pointer' : 'default')
    }
  },
  activeOutline: {
    borderColor: `${theme.palette.primary.main}`
  },
  rejectOutline: {
    borderColor: `${theme.palette.error.main}`
  }
}))

export interface UploadImageProps {
  classes?: StyleClasses<typeof useStyles>
  uploadOptions?: UploadOptions
  /**
   * use cloudinaryPublicId or s3PublicKey if possible,
   * but need to pass to make src for resizable image(s3 images)
   */
  src: string
  // Desired image sources
  cloudinaryPublicId?: string
  s3PublicKey?: string
  label?: string
  className?: string
  disabled?: boolean
  disabledNote?: string
  disableClear?: boolean
  showUploadButton?: boolean
  format?: string
  pages?: number
  accept?: string | string[]
  maxWidth?: number
  maxHeight?: number
  buttonHeight?: number
  quality?: number
  avatar?: boolean
  productImage?: boolean
  omitSizes?: string[]
  onFileSelected?: (file: File) => void
  onUpload: (args: {
    src: string
    cloudinaryPublicId: string
    s3PublicKey: string
    format: string
    pages: number
    width: number
    height: number
    type: string
  }) => any
  onClear: () => any
}

function UploadImage({
  className,
  src,
  cloudinaryPublicId,
  s3PublicKey,
  format,
  pages,
  label,
  disabled,
  disabledNote,
  disableClear,
  onClear,
  onUpload,
  maxWidth,
  maxHeight,
  buttonHeight,
  accept = PERMITTED_FILE_TYPES,
  uploadOptions,
  avatar = false,
  productImage = false,
  showUploadButton = false,
  omitSizes,
  onFileSelected,
  ...props
}: UploadImageProps) {
  const { __ } = useTranslation()
  const classes = useStyles({ buttonHeight, disabled, ...props })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const publicId = cloudinaryPublicId || s3PublicKey
  const { loading, handleDrop } = useImageUpload({
    maxWidth,
    maxHeight,
    uploadOptions,
    onUpload,
    onFileSelected,
    errorMessage: __('Unable to upload image')
  })

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div
      {...props}
      className={classnames(
        {
          [classes.root]: true,
          [classes.disabled]: !!disabled
        },
        className
      )}
    >
      <div className={classes.above}>
        {label && (
          <InputLabel shrink={true} className={classes.label}>
            {label}
          </InputLabel>
        )}
      </div>
      <Dropzone
        className={classnames(classes.dropzone, 'tour-upload-image-dropzone')}
        onDrop={handleDrop}
        disabled={loading || disabled}
        text={disabledNote ?? ''}
        accept={accept}
        buttonFullHeight={true}
        shouldRenderPreview={loading || !!publicId || !!src}
        renderPreview={({ isDragActive, draggedFiles, isDragReject }) => {
          const rejectDrag = isDragReject && draggedFiles.filter((file) => file.type !== '').length > 0
          return (
            <div className={classes.preview}>
              {loading ? (
                <div className={classes.previewOverlay}>
                  <Spinner fullWidth={true} fullHeight={true} />
                </div>
              ) : (
                <FilePreview
                  className={classnames({
                    [classes.filePreview]: true,
                    [classes.activeOutline]: isDragActive && !rejectDrag,
                    [classes.rejectOutline]: rejectDrag,
                    [classes.avatar]: avatar,
                    [classes.productImage]: productImage
                  })}
                  file={{ src, s3PublicKey, cloudinaryPublicId, type: 'image', format, pages, visibility: 'visible' }}
                  omitSizes={omitSizes}
                />
              )}
            </div>
          )
        }}
      />
      {!loading && !disabled && !disableClear && (
        <Button className={classes.clearButton} variant="text" disabled={!src && !publicId} onClick={onClear}>
          {__('Clear')}
        </Button>
      )}
      {!loading && !disabled && showUploadButton && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = e.target.files
              if (files && files.length > 0) {
                handleDrop(Array.from(files))
              }
            }}
          />
          <div
            className={classnames({
              [classes.clearButton]: true
            })}
          >
            <Button className={classes.clearButton} variant="outlined" onClick={handleButtonClick}>
              {__('+ Upload')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default UploadImage
